/**
 * HTML Sanitization Utility
 * 
 * Uses DOMPurify to sanitize HTML content before rendering with dangerouslySetInnerHTML.
 * This prevents XSS attacks while allowing safe HTML formatting.
 * 
 * @module utils/sanitize
 */

import DOMPurify from 'dompurify';

/**
 * Configuration for HTML sanitization
 * Allows common formatting tags while blocking dangerous content
 */
const SANITIZE_CONFIG = {
  // Allowed HTML tags for rich text content
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'b', 'i',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'span', 'div',
    'blockquote', 'pre', 'code',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'img'
  ],
  
  // Allowed attributes
  ALLOWED_ATTR: [
    'href',      // For links
    'class',     // For styling
    'style',     // For inline styles (can be restricted further if needed)
    'target',    // For link targets
    'rel',       // For link relationships
    'alt',       // For images
    'src',       // For images
    'title'      // For tooltips
  ],
  
  // Disallow data attributes to prevent XSS
  ALLOW_DATA_ATTR: false,
  
  // Additional security options
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
  
  // Sanitize style attributes
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * 
 * @param {string} dirty - Unsanitized HTML string
 * @param {Object} customConfig - Optional custom DOMPurify configuration
 * @returns {string} Sanitized HTML string safe for rendering
 * 
 * @example
 * import { sanitizeHTML } from '../utils/sanitize';
 * 
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(userContent) }} />
 */
export const sanitizeHTML = (dirty, customConfig = {}) => {
  // Return empty string for null/undefined
  if (!dirty) {
    return '';
  }
  
  // Convert to string if not already
  const htmlString = String(dirty);
  
  // Merge custom config with default config
  const config = {
    ...SANITIZE_CONFIG,
    ...customConfig
  };
  
  // Sanitize and return
  return DOMPurify.sanitize(htmlString, config);
};

/**
 * Sanitizes HTML with stricter rules for user-generated content
 * Use this for content that comes directly from user input
 * 
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} Sanitized HTML string
 */
export const sanitizeUserContent = (dirty) => {
  const strictConfig = {
    ...SANITIZE_CONFIG,
    // Remove style attribute for user content
    ALLOWED_ATTR: SANITIZE_CONFIG.ALLOWED_ATTR.filter(attr => attr !== 'style'),
    // Remove potentially dangerous tags
    ALLOWED_TAGS: SANITIZE_CONFIG.ALLOWED_TAGS.filter(
      tag => !['img', 'a'].includes(tag)
    )
  };
  
  return sanitizeHTML(dirty, strictConfig);
};

/**
 * Sanitizes plain text (strips all HTML)
 * Use this when you only need plain text output
 * 
 * @param {string} dirty - HTML string to convert to plain text
 * @returns {string} Plain text string
 */
export const sanitizeToText = (dirty) => {
  if (!dirty) {
    return '';
  }
  
  const config = {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  };
  
  return DOMPurify.sanitize(String(dirty), config);
};

/**
 * Removes text/background colour declarations from inline style attributes so
 * report body copy can be re-coloured with the active report theme.
 */
export const stripInlineColors = (html) => {
  if (!html) return '';

  return String(html).replace(/\s*style\s*=\s*(["'])(.*?)\1/gi, (match, quote, styleValue) => {
    const cleaned = styleValue
      .split(';')
      .map((declaration) => declaration.trim())
      .filter(Boolean)
      .filter((declaration) => !/^(?:color|background(?:-color)?)\s*:/i.test(declaration))
      .join('; ');

    if (!cleaned) return '';
    return ` style=${quote}${cleaned}${quote}`;
  });
};

const THEMED_TEXT_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
  'span',
  'div',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'blockquote',
  'td',
  'th',
]);

/**
 * Writes theme colours as real inline `style="color: …"` on text nodes.
 * Required for PDF export: htmlTemplate only links stylesheets in production, and the
 * export API does not reliably apply CSS variables / inheritance inside table HTML.
 */
export const applyInlineThemeColors = (html, { color, linkColor } = {}) => {
  if (!html || !color) return html || '';

  if (typeof DOMParser === 'undefined') {
    return String(html).replace(
      /<(p|h[1-6]|li|span|div|strong|em|b|i|u|blockquote|td|th)(\s[^>]*)?>/gi,
      (match, tag, attrs = '') => {
        if (/style\s*=/i.test(attrs)) {
          return `<${tag}${attrs.replace(/style\s*=\s*(["'])(.*?)\1/i, (_, q, styleValue) => {
            const withoutColor = styleValue
              .split(';')
              .map((d) => d.trim())
              .filter(Boolean)
              .filter((d) => !/^color\s*:/i.test(d))
              .join('; ');
            return ` style=${q}color: ${color};${withoutColor ? ` ${withoutColor}` : ''}${q}`;
          })}>`;
        }
        return `<${tag}${attrs} style="color: ${color}">`;
      }
    );
  }

  const doc = new DOMParser().parseFromString(`<div id="__report_html_root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__report_html_root');
  if (!root) return html;

  root.querySelectorAll('*').forEach((el) => {
    const tag = el.tagName.toLowerCase();
    if (tag === 'a') {
      if (linkColor) el.style.color = linkColor;
      return;
    }
    if (THEMED_TEXT_TAGS.has(tag)) el.style.color = color;
  });

  return root.innerHTML;
};

/**
 * Sanitizes HTML for report preview/export — strips CMS colours, then inlines the
 * active theme body/link colours so PDF export keeps Marsh navy (etc.) without CSS.
 *
 * @param {string} dirty
 * @param {{ color?: string, linkColor?: string } & Object} options
 */
export const sanitizeReportHTML = (dirty, options = {}) => {
  const { color, linkColor, ...customConfig } = options;
  const sanitized = stripInlineColors(sanitizeHTML(dirty, customConfig));
  return applyInlineThemeColors(sanitized, { color, linkColor });
};
/**
 * Validates if content is safe to render
 * Returns true if content passes sanitization without changes
 * 
 * @param {string} content - HTML content to validate
 * @returns {boolean} True if content is safe
 */
export const isContentSafe = (content) => {
  if (!content) {
    return true;
  }
  
  const sanitized = sanitizeHTML(content);
  return sanitized === content;
};

